'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { WhyPalmidosPoint } from '@/types/persona';
import { Card, CardContent } from '@/components/ui/card';
import DualCTA from './DualCTA';

interface WhyPalmidosProps {
  title: string;
  subtitle: string;
  points: WhyPalmidosPoint[];
  isRtl?: boolean;
  ctaText?: string;
  bookCallText?: string;
  whatsappText?: string;
  whatsappLink?: string;
}

const iconImages: Record<string, string> = {
  shield: '/revenue-card.svg',
  dollar: '/custom-made-solution-card.svg',
  rocket: '/ai-innovation-card.svg',
};

const titleVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
};

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
};

export default function WhyPalmidos({
  title,
  subtitle,
  points,
  isRtl = false,
  ctaText,
  bookCallText,
  whatsappText,
  whatsappLink,
}: WhyPalmidosProps) {
  return (
    <section className="w-full px-4 py-12 md:py-20">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          className="font-montserrat text-white mb-4 text-center leading-[130%]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={titleVariants}
        >
          <span className="block text-3xl md:text-4xl font-bold">{title}</span>
        </motion.h2>

        {subtitle && (
          <motion.p
            className="text-gray-400 text-center mb-12 text-lg"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={titleVariants}
          >
            {subtitle}
          </motion.p>
        )}

        <div className="flex justify-center items-center">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
          >
            {points.map((point, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{
                  scale: 1.03,
                  y: -5,
                }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  duration: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
              >
                <Card
                  className="inline-flex items-center border-none py-4 px-4 rounded-[0.86381rem] backdrop-blur-[16.58476448059082px] w-full max-w-lg cursor-pointer transition-all duration-300 hover:shadow-[0_0_35px_rgba(139,124,255,0.5),0_0_60px_rgba(139,124,255,0.3),inset_0px_2.073px_2.073px_rgba(255,255,255,0.25),inset_0px_74.631px_91.216px_rgba(139,124,255,0.4)]"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.08) -2.94%, rgba(255,255,255,0) 100%), rgba(255,255,255,0.02)",
                    boxShadow: "inset 0px 2.073px 2.073px rgba(255,255,255,0.16), inset 0px 74.631px 91.216px rgba(69,76,79,0.25)",
                  }}
                >
                  <CardContent className="flex items-center space-x-4 p-0">
                    <motion.div
                      className="flex-shrink-0"
                      whileHover={{ rotate: [0, -5, 5, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="overflow-hidden">
                        <Image
                          src={iconImages[point.icon]}
                          alt={point.title}
                          width={82}
                          height={82}
                        />
                      </div>
                    </motion.div>
                    <div className="flex flex-col space-y-1 items-start">
                      <p className="self-stretch text-white font-montserrat text-md md:text-[0.975rem] font-bold">
                        {point.title}
                      </p>
                      <p className="self-stretch text-white font-montserrat text-sm md:text-[0.975rem] font-light">
                        {point.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* CTA Section */}
        {ctaText && bookCallText && whatsappText && whatsappLink && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <p className="text-white/80 text-lg mb-6 font-montserrat">{ctaText}</p>
            <DualCTA
              bookCallText={bookCallText}
              whatsappText={whatsappText}
              whatsappLink={whatsappLink}
              variant="section"
              isRtl={isRtl}
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
